import { Plus } from "lucide-react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import { cn } from "@f2e/ui/lib/utils"
import { Card, ScrollArea } from "@f2e/ui/components/base"

import { PermissionButton } from "@/components/Auth"
import { OrganizationLimitUsageItem } from "@/hooks/api"
import { ClientChannelListItem, Workspace, Permission } from "@/types"

import { ChannelTypeBadge } from "./ChannelTypeBadge"

export interface ChannelListProps {
  channels: ClientChannelListItem[]
  selectedChannelItem?: ClientChannelListItem
  workspace: Workspace
  channelUsage?: OrganizationLimitUsageItem | null
  onChannelSelect: (channel: ClientChannelListItem) => void
  onCreateChannel: () => void
}

const formatCount = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

export const ChannelUsageCard = ({
  usage,
}: {
  usage?: OrganizationLimitUsageItem | null
}) => {
  const { t } = useTranslation()
  if (!usage) return null

  const used = usage.used
  const limit = usage.limit ?? null
  const atLimit = usage.atLimit
  const percent =
    limit == null
      ? null
      : limit <= 0
        ? 100
        : Math.min(100, (used / limit) * 100)
  const limitLabel =
    limit == null ? t("workspace.channels.usage.unlimited") : formatCount(limit)

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900/70">
      <div className="flex items-center justify-between gap-3 text-xs">
        <div className="min-w-0">
          <div className="font-medium text-slate-700 dark:text-slate-200">
            {t("workspace.channels.usage.title")}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            {t("workspace.channels.usage.description")}
          </div>
        </div>
        <span className="shrink-0 text-slate-500 dark:text-slate-400">
          {t("workspace.channels.usage.value", {
            used: formatCount(used),
            limit: limitLabel,
          })}
        </span>
      </div>
      {percent != null && (
        <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
          <div
            className={cn(
              "h-2 rounded-full transition-all",
              atLimit
                ? "bg-red-500"
                : percent >= 80
                  ? "bg-amber-500"
                  : "bg-emerald-500"
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {atLimit && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {t("workspace.channels.usage.atLimit")}
        </p>
      )}
    </div>
  )
}

export const ChannelList = ({
  channels,
  selectedChannelItem,
  workspace,
  channelUsage,
  onChannelSelect,
  onCreateChannel,
}: ChannelListProps) => {
  const { t } = useTranslation()
  return (
    <Card className="flex flex-col h-auto lg:h-full">
      <div className="flex flex-col space-y-3 p-4 h-[240px] lg:h-full shrink-0">
        {/* New Channel Button */}
        <PermissionButton
          permission={Permission.WsUpdateResources}
          workspace={workspace}
          disabledTooltip={t("common.noPermission")}
          onClick={onCreateChannel}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("workspace.channels.list.newChannel")}
        </PermissionButton>

        <ChannelUsageCard usage={channelUsage} />

        {/* Channel Cards */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="space-y-2">
              {channels.map((channel) => (
                <Card
                  key={channel.id}
                  className={cn(
                    "p-4 cursor-pointer transition-all hover:shadow-md",
                    selectedChannelItem?.id === channel.id
                      ? "border-primary! bg-primary/5! dark:bg-primary/5!"
                      : "border-border!"
                  )}
                  onClick={() => onChannelSelect(channel)}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <h3 className="font-semibold text-sm truncate flex-1">
                        {channel.name}
                      </h3>
                      <ChannelTypeBadge type={channel.type} className="ml-2" />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {channel.slug}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {channel.lastPublishedVersion
                          ? `v${channel.lastPublishedVersion}`
                          : "-"}
                      </span>
                      <span>
                        {channel.publishedAt
                          ? format(channel.publishedAt, "MMM d, yyyy")
                          : t(
                              "workspace.channels.overview.publishStatus.notPublished"
                            )}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </Card>
  )
}

export default ChannelList
